import type { TFunction } from 'i18next';
import type { AdapterConfigField, AdapterKind } from '../types';

/** Picker order from the design (AddIntegration screen). */
export const ADAPTER_ORDER: AdapterKind[] = [
  'generic_webhook',
  'zapier_webhook',
  'slack_url',
  'discord_url',
  'google_sheets_webhook',
  'email',
  'formshive_endpoint',
  'api_pipedrive',
  'api_mailchimp',
  'api_kit',
];

// The backend ignores the submit token, so it is not asked for.
const HIDDEN_FIELDS: Partial<Record<AdapterKind, string[]>> = {
  formshive_endpoint: ['submit_token'],
};

const base = (kind: string) => `integrations.adapter.${kind}`;

export function adapterName(kind: string, t: TFunction): string {
  return t(`${base(kind)}.name`, { defaultValue: kind });
}

export function adapterBlurb(kind: string, t: TFunction): string {
  return t(`${base(kind)}.blurb`, { defaultValue: '' });
}

export function adapterTitleHint(kind: string, t: TFunction): string {
  return t(`${base(kind)}.titleHint`, { defaultValue: '' });
}

export function isFieldShown(kind: string, field: AdapterConfigField): boolean {
  return !(HIDDEN_FIELDS[kind as AdapterKind] ?? []).includes(field.key);
}

export interface FieldCopy {
  label: string;
  help?: string;
  placeholder?: string;
}

/** Design copy for a field where it exists, else the registry's own label and help. */
export function adapterFieldCopy(kind: string, field: AdapterConfigField, t: TFunction): FieldCopy {
  const key = `${base(kind)}.fields.${field.key}`;
  const label = t(`${key}.label`, { defaultValue: '' }) || t(field.label);
  const help =
    t(`${key}.help`, { defaultValue: '' }) || (field.helperText ? t(field.helperText) : undefined);
  const placeholder = t(`${key}.placeholder`, { defaultValue: '' }) || field.placeholder;
  return { label, help: help || undefined, placeholder: placeholder || undefined };
}

const ERROR_OVERRIDES: Record<string, string> = {
  'integrations.errors.emailListEmpty': 'integrations.editor.sendToEmpty',
  'integrations.errors.emailInvalid': 'integrations.editor.sendToInvalid',
};

/** Registry validators return i18n keys; a few name the old "Recipients" wording. */
export function validationMessage(key: string, t: TFunction): string {
  return t(ERROR_OVERRIDES[key] ?? key);
}
